import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AppointmentsCardsComponent } from '../components/appointments-cards/appointments-cards.component';
import { AppointmentsToolbarComponent } from '../components/appointments-toolbar/appointments-toolbar.component';
import { AppointmentsTableComponent } from '../components/appointments-table/appointments-table.component';
import { AppointmentsCalendarComponent } from '../components/appointments-calendar/appointments-calendar.component';
import { ScheduleAppointmentModalComponent } from '../components/schedule-appointment-modal/schedule-appointment-modal.component';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../services/profile.service';
import { PackageService } from '../services/package.service';
import { AppointmentService } from '../services/appointment.service';
import { BaseAppointmentsComponent } from './base-appointments.component';

/**
 * AdminAppointmentsComponent - Administrative appointments management view combining list, calendar, and scheduling modal.
 * This component extends BaseAppointmentsComponent to provide administrators with a unified interface for viewing, scheduling, and refreshing appointments. It coordinates child components (appointments cards, toolbar, table, calendar, and schedule modal), toggles between list and calendar views (onViewChanged), controls the schedule appointment modal visibility (openScheduleAppointmentModal / closeScheduleAppointmentModal), and refreshes appointment data and statistics when appointments are created or updated (onAppointmentCreated / onAppointmentUpdated). Lifecycle: implements OnInit and OnDestroy (initialization and cleanup are handled, with additional behavior inherited from BaseAppointmentsComponent).
 * @component
 * @selector app-admin-appointments
 * @example
 *   <app-admin-appointments></app-admin-appointments>
 * @input none - No public @Input properties; this component relies on injected services and internal state (view selection, current location, and modal visibility).
 * @output none - No public @Output EventEmitters; updates are propagated by calling child component APIs and via shared services.
 */
@Component({
  selector: 'app-admin-appointments',
  imports: [
    AppointmentsCardsComponent,
    AppointmentsToolbarComponent,
    AppointmentsTableComponent,
    AppointmentsCalendarComponent,
    ScheduleAppointmentModalComponent,
    CommonModule,
  ],
  templateUrl: './appointments-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-appointments.component.scss',
})
export class AdminAppointmentsComponent
  extends BaseAppointmentsComponent
  implements OnInit, OnDestroy
{
  readonly isAdminUser = true;

  constructor(
    profileService: ProfileService,
    packageService: PackageService,
    appointmentService: AppointmentService
  ) {
    super(profileService, packageService, appointmentService);
  }

  /**
   * Handle switching between 'list' and 'calendar' views, update current view state, ensure a default location label when entering calendar view, and trigger calendar appointment loading.
   * @example
   * onViewChanged('calendar')
   * undefined
   * @param {{('list'|'calendar')}} {{view}} - The view to switch to; either 'list' or 'calendar'.
   * @returns {{void}} No return value.
   **/
  override onViewChanged(view: 'list' | 'calendar'): void {
    this.currentView = view;
    if (view === 'calendar') {
      // Deliberately does not seed a location any more. It used to write locations[0] into the
      // page-level selection, which then followed the user back to the list and re-scoped it to
      // one branch. The calendar still picks its own default from locations$ when it is handed
      // an empty selection, so switching views no longer narrows the list behind the user.
      if (this.appointmentsCalendar) {
        setTimeout(() => {
          this.appointmentsCalendar.loadAppointments();
        }, 100);
      }
    }
  }
}
